import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatarAteDuasCasas, formatarReais } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe, type AlvoDoRetorno, type RetornoDeclarado } from '@/lib/retorno';

import {
  api,
  type CadastrosDaOficina,
  type DetalheDaOrdem,
  type EnvioDaParada,
  type EnvioDeEncerramento,
  type EnvioDeLiberacao,
  type EnvioDeOrdem,
  type Etapa,
  type OrdemDaApi,
} from './api';
import { numeroDaOrdem, ROTULO_DA_ETAPA, ROTULO_DO_TIPO } from './ordem';
import { textoDaJanela } from './parada';

export { explicarFalha };

/**
 * O módulo 06 falando em domínio.
 *
 * Toda escrita invalida a O.S. E a manutenção: liberar uma preventiva muda o
 * vencimento da máquina, e a fila de manutenção mostraria "vencida" de uma
 * tarefa que acabou de ser feita. A invalidação grossa é o que impede isso.
 */
const CHAVE = ['oficina'] as const;

export function useCadastrosDaOficina() {
  return useQuery({ queryKey: [...CHAVE, 'cadastros'], queryFn: () => api.cadastros() });
}

/** `habilitada` falso não pede: a janela de outro módulo que só escolhe uma O.S. pergunta quando abre. */
export function useOrdens(habilitada = true) {
  return useQuery({ queryKey: [...CHAVE, 'ordens'], enabled: habilitada, queryFn: async () => (await api.ordens()).ordens });
}

/**
 * A agenda das paradas programadas (0089): que máquina sai da frente no dia.
 * Pede `operacao.ler` — é o que a Operação vê; quem não tem, não pede.
 */
export function useParadasProgramadas(habilitada = true, dia?: string) {
  return useQuery({
    queryKey: [...CHAVE, 'paradas-programadas', dia ?? 'hoje'],
    enabled: habilitada,
    queryFn: async () => (await api.paradasProgramadas(dia)).paradas,
  });
}

export function useOrdem(id: string | null) {
  return useQuery({ queryKey: [...CHAVE, 'ordem', id], enabled: id !== null, queryFn: () => api.ordem(id ?? '') });
}

/** O que a tela já leu da oficina — o "em quê" do retorno (R04, p.32). */
interface LidoDaOficina {
  readonly ordem: (id: string) => OrdemDaApi | undefined;
  readonly detalhes: () => readonly DetalheDaOrdem[];
  readonly cadastros: CadastrosDaOficina | undefined;
}

/** A O.S. como alvo: "O.S. 000124 · ESC-001". */
function alvoDaOrdem(o: OrdemDaApi | undefined): { alvo?: AlvoDoRetorno } {
  return o ? { alvo: { codigo: numeroDaOrdem(o.numero), nome: `${o.equipamento} · ${o.equipamentoDescricao}` } } : {};
}

/**
 * Toda escrita da oficina declara o seu retorno (R04, p.32). A resposta traz
 * o id ou a etapa; o número da O.S. e a máquina vêm do que a tela já leu.
 */
function useEscrita<A, R>(
  fazer: (argumento: A) => Promise<R>,
  montar: (resposta: R, argumento: A, lido: LidoDaOficina) => RetornoDeclarado,
) {
  const cliente = useQueryClient();
  const lido: LidoDaOficina = {
    ordem: (id) =>
      cliente.getQueryData<readonly OrdemDaApi[]>([...CHAVE, 'ordens'])?.find((o) => o.id === id) ??
      cliente.getQueryData<DetalheDaOrdem>([...CHAVE, 'ordem', id])?.ordem,
    detalhes: () =>
      cliente
        .getQueriesData<DetalheDaOrdem>({ queryKey: [...CHAVE, 'ordem'] })
        .flatMap(([, d]) => (d ? [d] : [])),
    get cadastros() {
      return cliente.getQueryData<CadastrosDaOficina>([...CHAVE, 'cadastros']);
    },
  };
  return useMutation({
    meta: { retorno: retornoDe<R, A>((r, a) => montar(r, a, lido)) },
    mutationFn: fazer,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CHAVE });
      void cliente.invalidateQueries({ queryKey: ['manutencao'] });
      void cliente.invalidateQueries({ queryKey: ['frota'] });
      void cliente.invalidateQueries({ queryKey: ['estoque'] });
    },
  });
}

export const useAbrirOrdem = () =>
  useEscrita(
    (envio: EnvioDeOrdem) => api.abrir(envio),
    (r, v, lido) => {
      const maquina = lido.cadastros?.equipamentos.find((e) => e.id === v.equipamentoId);
      return {
        titulo: 'O.S. aberta',
        alvo: { codigo: numeroDaOrdem(r.numero), ...(maquina ? { nome: `${maquina.codigo} · ${maquina.descricao}` } : {}) },
        fatos: [ROTULO_DO_TIPO[v.tipo], v.sintoma, v.maquinaParada ? 'máquina parada' : null],
      };
    },
  );
export const useAjustarOrdem = () =>
  useEscrita(
    ({ id, envio }: { id: string; envio: Parameters<typeof api.ajustar>[1] }) => api.ajustar(id, envio),
    (_r, v, lido) => {
      const responsavel = lido.cadastros?.pessoas.find((p) => p.id === v.envio.responsavelId);
      return {
        titulo: 'O.S. alterada',
        ...alvoDaOrdem(lido.ordem(v.id)),
        fatos: [
          v.envio.diagnostico ? `diagnóstico: ${v.envio.diagnostico}` : null,
          v.envio.causaRaiz ? `causa raiz: ${v.envio.causaRaiz}` : null,
          responsavel ? `responsável: ${responsavel.nome}` : null,
        ],
      };
    },
  );
export const useMudarEtapa = () =>
  useEscrita(
    ({ id, etapa, motivo }: { id: string; etapa: Etapa; motivo?: string }) => api.mudarEtapa(id, etapa, motivo),
    (r, v, lido) => {
      const ordem = lido.ordem(v.id);
      return {
        titulo: 'Etapa da O.S. mudada',
        ...alvoDaOrdem(ordem),
        fatos: [v.motivo ? `motivo: ${v.motivo}` : null],
        efeito: { rotulo: 'Etapa', ...(ordem ? { antes: ROTULO_DA_ETAPA[ordem.etapa] } : {}), depois: ROTULO_DA_ETAPA[r.etapa] },
      };
    },
  );
export const useLancarMaoDeObra = () =>
  useEscrita(
    ({ id, envio }: { id: string; envio: Parameters<typeof api.maoDeObra>[1] }) => api.maoDeObra(id, envio),
    (_r, v, lido) => {
      const mecanico = lido.cadastros?.pessoas.find((p) => p.id === v.envio.mecanicoId);
      const horas = (Date.parse(v.envio.fim) - Date.parse(v.envio.inicio)) / 3_600_000;
      return {
        titulo: 'Mão de obra lançada',
        ...alvoDaOrdem(lido.ordem(v.id)),
        fatos: [mecanico?.nome ?? null, Number.isFinite(horas) ? `${formatarAteDuasCasas(horas)} h` : null, v.envio.descricao ?? null],
      };
    },
  );
export const useLancarPeca = () =>
  useEscrita(
    ({ id, envio }: { id: string; envio: Parameters<typeof api.peca>[1] }) => api.peca(id, envio),
    (_r, v, lido) => {
      const deposito = lido.cadastros?.depositos.find((d) => d.id === v.envio.depositoId);
      return {
        titulo: v.envio.situacao === 'aplicada' ? 'Peça aplicada na O.S.' : 'Peça prevista na O.S.',
        ...alvoDaOrdem(lido.ordem(v.id)),
        fatos: [v.envio.descricao, `${formatarAteDuasCasas(v.envio.quantidade)} ${v.envio.unidade}`],
        ...(deposito
          ? { efeito: v.envio.situacao === 'aplicada' ? `Baixou do ${deposito.nome}` : `Reservada no ${deposito.nome}` }
          : {}),
      };
    },
  );
export const useAplicarPeca = () =>
  useEscrita(
    (pecaId: string) => api.aplicarPeca(pecaId),
    (_r, pecaId, lido) => {
      const detalhe = lido.detalhes().find((d) => d.pecas.some((p) => p.id === pecaId));
      const peca = detalhe?.pecas.find((p) => p.id === pecaId);
      return {
        titulo: 'Peça aplicada na O.S.',
        ...alvoDaOrdem(detalhe?.ordem),
        fatos: [peca?.descricao ?? null],
        efeito: 'A reserva virou baixa no estoque',
      };
    },
  );
export const usePedirHipotese = () =>
  useEscrita(
    (id: string) => api.pedirHipotese(id),
    (r, id, lido) => ({
      titulo: r.sabe ? 'Hipótese da IA pronta' : 'A IA não chegou a uma hipótese',
      tom: r.sabe ? 'sucesso' : 'ressalva',
      ...alvoDaOrdem(lido.ordem(id)),
      fatos: [r.falta ? `falta: ${r.falta}` : null],
      ...(r.gravadaNaOrdem ? { efeito: 'Ficou gravada na O.S.' } : {}),
    }),
  );

export const useAnexar = () =>
  useEscrita(
    ({ id, arquivo, descricao }: { id: string; arquivo: File; descricao: string }) => api.anexar(id, arquivo, descricao),
    (_r, v, lido) => ({
      titulo: 'Anexo guardado na O.S.',
      ...alvoDaOrdem(lido.ordem(v.id)),
      fatos: [v.descricao, v.arquivo.name],
    }),
  );

/**
 * Abre o anexo numa aba nova. A aba abre ANTES do pedido da URL, no clique:
 * aberta depois de um `await`, o navegador a trataria como janela não pedida
 * e a bloquearia. É o mesmo jeito da biblioteca técnica.
 */
export async function abrirAnexo(anexoId: string): Promise<void> {
  const aba = window.open('about:blank', '_blank');
  try {
    const { url } = await api.urlDoAnexo(anexoId);
    if (aba) {
      aba.opener = null;
      aba.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (erro) {
    aba?.close();
    throw erro;
  }
}

/**
 * PROGRAMAR A PARADA com a operação (0089). Reprogramar é gravar de novo: o
 * efeito diz a janela de antes e a de agora.
 */
export const useProgramarParada = () =>
  useEscrita(
    ({ id, envio }: { id: string; envio: EnvioDaParada }) => api.programarParada(id, envio),
    (_r, v, lido) => {
      const ordem = lido.ordem(v.id);
      const com = lido.cadastros?.pessoas.find((p) => p.id === v.envio.combinadaComId);
      const antes = ordem?.paradaProgramada
        ? textoDaJanela(new Date(ordem.paradaProgramada.de), new Date(ordem.paradaProgramada.ate))
        : null;
      const depois = textoDaJanela(new Date(v.envio.de), new Date(v.envio.ate));
      return {
        titulo: antes ? 'Parada reprogramada' : 'Parada programada',
        ...alvoDaOrdem(ordem),
        fatos: [com ? `combinada com ${com.nome}` : null],
        efeito: antes ? { rotulo: 'Janela', antes, depois } : `A máquina sai da operação em ${depois}`,
      };
    },
  );

export const useLancarServico = () =>
  useEscrita(
    ({ id, envio }: { id: string; envio: Parameters<typeof api.servico>[1] }) => api.servico(id, envio),
    (_r, v, lido) => {
      const fornecedor = lido.cadastros?.fornecedores.find((f) => f.id === v.envio.fornecedorId);
      return {
        titulo: 'Serviço de terceiro lançado',
        ...alvoDaOrdem(lido.ordem(v.id)),
        fatos: [fornecedor?.nome ?? null, v.envio.descricao, v.envio.valor ? formatarReais(v.envio.valor) : null],
      };
    },
  );
export const useLiberar = () =>
  useEscrita(
    ({ id, envio }: { id: string; envio: EnvioDeLiberacao }) => api.liberar(id, envio),
    (r, v, lido) => {
      const ordem = lido.ordem(v.id);
      return {
        titulo: 'Máquina liberada',
        ...alvoDaOrdem(ordem),
        fatos: [
          v.envio.solucao,
          v.envio.causaRaiz ? `causa raiz: ${v.envio.causaRaiz}` : null,
          v.envio.leitura === null || v.envio.leitura === undefined ? null : `leitura ${formatarAteDuasCasas(v.envio.leitura)}`,
        ],
        efeito: { rotulo: 'Etapa', ...(ordem ? { antes: ROTULO_DA_ETAPA[ordem.etapa] } : {}), depois: ROTULO_DA_ETAPA[r.etapa] },
      };
    },
  );
export const useEncerrar = () =>
  useEscrita(
    ({ id, envio }: { id: string; envio?: EnvioDeEncerramento }) => api.encerrar(id, envio),
    (r, v, lido) => {
      const ordem = lido.ordem(v.id);
      return {
        titulo: 'O.S. encerrada',
        ...alvoDaOrdem(ordem),
        fatos: [v.envio?.causaRaiz ? `causa raiz: ${v.envio.causaRaiz}` : null],
        efeito: { rotulo: 'Etapa', ...(ordem ? { antes: ROTULO_DA_ETAPA[ordem.etapa] } : {}), depois: ROTULO_DA_ETAPA[r.etapa] },
      };
    },
  );
export const useCancelar = () =>
  useEscrita(
    ({ id, motivo }: { id: string; motivo: string }) => api.cancelar(id, motivo),
    (r, v, lido) => {
      const ordem = lido.ordem(v.id);
      return {
        titulo: 'O.S. cancelada',
        ...alvoDaOrdem(ordem),
        fatos: [`motivo: ${v.motivo}`],
        efeito: { rotulo: 'Etapa', ...(ordem ? { antes: ROTULO_DA_ETAPA[ordem.etapa] } : {}), depois: ROTULO_DA_ETAPA[r.etapa] },
      };
    },
  );
